import { useEffect } from 'react';
import { profileApi, ApiError } from '@/api/client';
import { isViewingAsSomeoneElse } from '@/lib/viewAs';

/****************
 * THE REFERRAL CODE, FROM THE LINK OR THE FIELD TO THE ACCOUNT. 2026-09-26.
 *
 * app.copyfi.ai/?ref=CF-7KQ2MX on any page is remembered on this device until
 * an account exists; so is a code typed under the sign-up form. Once signed
 * in, it is sent once (backend referral.go decides: once, never your own
 * code, only in the account's first days) and forgotten whatever the answer,
 * except when the server could not be reached.
 ****************/
export const PENDING_REFERRAL_KEY = 'copyfi.referral.pending';

type Pending = { code: string; source: 'link' | 'code' };

export function readPendingReferral(): Pending | null {
  try {
    const v = JSON.parse(localStorage.getItem(PENDING_REFERRAL_KEY) || 'null');
    return v && typeof v.code === 'string' && v.code.trim() ? v : null;
  } catch { return null; }
}

export function savePendingReferral(code: string, source: Pending['source']) {
  try {
    const c = code.trim().toUpperCase();
    if (c) localStorage.setItem(PENDING_REFERRAL_KEY, JSON.stringify({ code: c, source }));
    else localStorage.removeItem(PENDING_REFERRAL_KEY);
  } catch { /* no storage: the link is lost, an admin can set it */ }
}

function forgetPendingReferral() {
  try { localStorage.removeItem(PENDING_REFERRAL_KEY); } catch { /* nothing to forget */ }
}

/** Called once at start: a ?ref= in the address is remembered. */
export function captureReferralFromUrl() {
  try {
    const code = new URLSearchParams(window.location.search).get('ref');
    if (code) savePendingReferral(code, 'link');
  } catch { /* no URL: nothing to capture */ }
}

/** Signed in with a remembered code: attach it, once. */
export function useClaimPendingReferral(userId: string | null | undefined) {
  useEffect(() => {
    if (!userId || isViewingAsSomeoneElse()) return;
    const pending = readPendingReferral();
    if (!pending) return;
    profileApi.claimReferral(pending.code, pending.source)
      .then(forgetPendingReferral)
      .catch((e) => {
        // The server answered (unknown code, own code, already referred, too
        // late): nothing will change by retrying. Unreachable: keep it.
        if (e instanceof ApiError && e.answeredByServer && e.status < 500) forgetPendingReferral();
      });
  }, [userId]);
}
